/**
 * RunTimelineTab — merged timeline from server (llm_calls + tool_executions + existing entries).
 * OBS-6 stage 2 — per-run timeline view for WorkflowRunDetail.
 *
 * Each entry shows: time / kind badge / duration / label.
 * Clicking an 'llm_call' or 'tool_execution' entry opens an inline detail panel.
 */
import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Clock, Cpu, Wrench } from 'lucide-react';
import { Badge } from '../../../components/ui/Badge.js';
import { Callout } from '../../../components/ui/Callout.js';
import { type DebugPayload, formatTime, formatDuration, formatCost } from './run-detail-types.js';

interface RunTimelineTabProps {
  payload: DebugPayload;
}

type TimelineEntry = DebugPayload['timeline'][number];

// Kind → badge variant + icon
function kindMeta(kind: string): { label: string; variant: 'accent' | 'neutral' | 'warning'; Icon: React.ElementType } {
  switch (kind) {
    case 'llm_call':
      return { label: 'LLM', variant: 'accent', Icon: Cpu };
    case 'tool_execution':
      return { label: 'Tool', variant: 'warning', Icon: Wrench };
    default:
      return { label: kind, variant: 'neutral', Icon: Clock };
  }
}

type LlmCall = NonNullable<DebugPayload['llmCalls']>['calls'][number];

interface LlmCallDetailProps {
  call: LlmCall;
}

const LlmCallDetail: React.FC<LlmCallDetailProps> = ({ call }) => {
  const hasContent = call.systemPromptRef != null || call.messagesRef != null || call.responseRef != null;

  return (
    <div className="mt-2 ml-8 bg-bg border border-border rounded-lg p-3 space-y-2 text-[12px]">
      {/* Meta row */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11px] text-muted">
        <span>
          model: <span className="text-body">{call.model}</span>
        </span>
        {call.modelVersion && (
          <span>
            ver: <span className="text-body">{call.modelVersion}</span>
          </span>
        )}
        <span>
          provider: <span className="text-body">{call.provider}</span>
        </span>
        {call.temperature != null && (
          <span>
            temp: <span className="text-body">{call.temperature}</span>
          </span>
        )}
        {call.maxTokens != null && (
          <span>
            max_tokens: <span className="text-body">{call.maxTokens}</span>
          </span>
        )}
      </div>

      {/* Token / cost row */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11px] text-muted">
        {call.promptTokens != null && (
          <span>
            prompt_tokens: <span className="text-body">{call.promptTokens.toLocaleString()}</span>
          </span>
        )}
        {call.completionTokens != null && (
          <span>
            completion_tokens: <span className="text-body">{call.completionTokens.toLocaleString()}</span>
          </span>
        )}
        {call.costUsd != null && (
          <span>
            cost: <span className="text-body">{formatCost(call.costUsd)}</span>
          </span>
        )}
        {call.durationMs != null && (
          <span>
            latency: <span className="text-body">{formatDuration(call.durationMs)}</span>
          </span>
        )}
        {call.finishReason && (
          <span>
            finish: <span className="text-body">{call.finishReason}</span>
          </span>
        )}
      </div>

      {/* Content refs — gated by CanManageWorkflows (server returns null for non-admin) */}
      {!hasContent ? (
        <Callout tone="accent">
          Full I/O (system prompt, messages, response) requires the <strong>manage-workflows</strong> permission. Switch
          detail level to &quot;full&quot; if you have access.
        </Callout>
      ) : (
        <div className="space-y-2">
          {call.systemPromptRef && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-faint mb-1">System prompt</div>
              <pre className="text-[11px] bg-surface-alt rounded-sm p-2 overflow-x-auto whitespace-pre-wrap max-h-48 overflow-y-auto">
                {call.systemPromptRef}
              </pre>
            </div>
          )}
          {call.messagesRef && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-faint mb-1">Messages</div>
              <pre className="text-[11px] bg-surface-alt rounded-sm p-2 overflow-x-auto whitespace-pre-wrap max-h-48 overflow-y-auto">
                {call.messagesRef}
              </pre>
            </div>
          )}
          {call.responseRef && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-faint mb-1">Response</div>
              <pre className="text-[11px] bg-surface-alt rounded-sm p-2 overflow-x-auto whitespace-pre-wrap max-h-48 overflow-y-auto">
                {call.responseRef}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface TimelineRowProps {
  entry: TimelineEntry;
  llmCall?: LlmCall;
}

const TimelineRow: React.FC<TimelineRowProps> = ({ entry, llmCall }) => {
  const [open, setOpen] = useState(false);
  const { label: kindLabel, variant, Icon } = kindMeta(entry.kind ?? entry.type);
  const isExpandable = (entry.kind === 'llm_call' || entry.kind === 'tool_execution') && !!llmCall;

  const severityTextClass = (() => {
    if (entry.severity === 'error') return 'text-danger';
    if (entry.severity === 'warning') return 'text-warning';
    return 'text-body';
  })();

  return (
    <div className="border-b border-border last:border-0">
      <div
        className={[
          'grid gap-3 items-start px-3 py-2 text-[12px]',
          isExpandable ? 'cursor-pointer hover:bg-surface-hover' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={{ gridTemplateColumns: '72px 48px minmax(0,1fr) 60px' }}
        onClick={isExpandable ? () => setOpen((o) => !o) : undefined}
        role={isExpandable ? 'button' : undefined}
        aria-expanded={isExpandable ? open : undefined}
      >
        <span className="font-mono text-[10.5px] text-faint shrink-0 pt-0.5">{formatTime(entry.at)}</span>
        <span className="shrink-0 pt-0.5">
          <Badge variant={variant}>
            <Icon size={9} />
            {kindLabel}
          </Badge>
        </span>
        <span className={severityTextClass + ' truncate'}>
          {entry.stepId && <span className="text-accent-text mr-1.5 font-mono text-[10.5px]">{entry.stepId}</span>}
          {entry.label}
        </span>
        <span className="text-right font-mono text-[10.5px] text-faint shrink-0">
          {llmCall?.durationMs != null ? formatDuration(llmCall.durationMs) : ''}
          {isExpandable && (
            <span className="ml-1 text-muted">
              {open ? <ChevronDown size={12} className="inline" /> : <ChevronRight size={12} className="inline" />}
            </span>
          )}
        </span>
      </div>

      {open && isExpandable && llmCall && <LlmCallDetail call={llmCall} />}
    </div>
  );
};

export const RunTimelineTab: React.FC<RunTimelineTabProps> = ({ payload }) => {
  const { timeline, llmCalls } = payload;

  // Build refId → LlmCall lookup
  const llmCallById = new Map<string, LlmCall>();
  if (llmCalls) {
    for (const call of llmCalls.calls) {
      llmCallById.set(call.id, call);
    }
  }

  return (
    <div className="bg-surface border border-border rounded-xl overflow-hidden mb-4" style={{ margin: 0 }}>
      {/* Card header */}
      <div className="flex items-center justify-between gap-2.5 px-4 py-3 border-b border-border bg-surface-alt">
        <span className="font-mono text-[10.5px] uppercase tracking-[1.2px] text-muted flex items-center gap-2">
          <Clock size={14} className="text-faint" />
          Run timeline
        </span>
        {llmCalls && (
          <span className="font-mono text-[10.5px] text-faint">
            {llmCalls.totalCount} LLM call{llmCalls.totalCount !== 1 ? 's' : ''}
            {llmCalls.totalCount > llmCalls.cappedAt && ` (capped at ${llmCalls.cappedAt})`}
          </span>
        )}
      </div>

      {timeline.length === 0 ? (
        <div className="p-4 text-[12.5px] text-muted">No timeline entries for this run.</div>
      ) : (
        <div className="divide-y divide-border">
          {/* Column headers */}
          <div
            className="grid gap-3 px-3 py-1.5 font-mono text-[9.5px] uppercase tracking-[0.6px] text-faint bg-surface-alt"
            style={{ gridTemplateColumns: '72px 48px minmax(0,1fr) 60px' }}
          >
            <span>Time</span>
            <span>Kind</span>
            <span>Event</span>
            <span className="text-right">Duration</span>
          </div>
          {timeline.map((entry, idx) => {
            const llmCall = entry.refId ? llmCallById.get(entry.refId) : undefined;
            return (
              <TimelineRow key={`${entry.kind ?? entry.type}-${entry.at}-${idx}`} entry={entry} llmCall={llmCall} />
            );
          })}
        </div>
      )}
    </div>
  );
};
